<script setup>
  import { ref, nextTick, watch, onMounted, onUnmounted, getCurrentInstance } from "vue";
  import { storeToRefs } from "pinia";
  import TwoPanePage from "@templates/TwoPanePage.vue";
  import ThreePanePage from "@templates/ThreePanePage.vue";
  import AiInsightConversationList from "@/components/Survey/Assistant/AiInsightConversationList.vue";
  import AiInsightConversationEdit from "@/components/Survey/Assistant/AiInsightConversationEdit.vue";
  import InterviewDetailPanel from "@/components/Survey/Grids/InterviewDetailPanel.vue";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
  import { useAiInsightConversationMessagesStore } from "@stores/survey/assistant/aiInsightConversationMessages";
  import { useQuoteSourceStore } from "@stores/survey/assistant/quoteSource";

  const instance = getCurrentInstance();
  const cable = instance.proxy.$cable;

  const surveyStore = useSurveyStore();
  const { isLiveOrComplete, token } = storeToRefs(surveyStore);

  const aiInsightConversationMessagesStore = useAiInsightConversationMessagesStore();
  const { channelHandlers } = aiInsightConversationMessagesStore;

  const { selectedConversation } = storeToRefs(useAiInsightConversationsStore());

  const quoteSourceStore = useQuoteSourceStore();
  const { isPanelOpen, selectedInterview, objectives, pendingMessageId } = storeToRefs(quoteSourceStore);

  const detailPanelRef = ref(null);
  const scrollToMessageId = ref(null);

  // Once the panel is open on the resolved interview, switch to the conversation
  // tab and trigger the scroll to the cited message (none for legacy citations).
  watch([isPanelOpen, selectedInterview], async ([open]) => {
    if (!open) {
      scrollToMessageId.value = null;
      return;
    }
    await nextTick();
    if (!detailPanelRef.value) return;
    detailPanelRef.value.activeTab = "conversation";
    scrollToMessageId.value = pendingMessageId.value;
  });

  // Switching thread closes the source drawer.
  watch(selectedConversation, () => quoteSourceStore.close());

  onMounted(() => {
    cable.subscribe({ channel: "AnalysisAssistantChannel", survey_token: token.value });
    cable._addChannel("AnalysisAssistantChannel", channelHandlers(cable, token.value), {
      _uid: instance.uid,
      _value: instance.proxy,
    });
  });

  onUnmounted(() => {
    cable.unsubscribe("AnalysisAssistantChannel");
  });
</script>

<template>
  <TwoPanePage
    v-if="isLiveOrComplete"
    :span-left="3"
    :span-right="9"
    data-cy="survey-assistant-page">
    <template #left>
      <AiInsightConversationList />
    </template>
    <template #right>
      <AiInsightConversationEdit />
    </template>
  </TwoPanePage>
  <ThreePanePage
    v-else
    :span-center="9"
    :span-right="0"
    data-cy="survey-assistant-page">
    <template #center>
      <AiInsightConversationEdit />
    </template>
  </ThreePanePage>

  <InterviewDetailPanel
    ref="detailPanelRef"
    v-model:open="isPanelOpen"
    :interview="selectedInterview"
    :objectives="objectives"
    :scroll-to-message-id="scrollToMessageId" />
</template>
